import { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import get from 'lodash/get';
import flow from 'lodash/flow';
import {
  closeNowPlayingDialogAndTrack,
  closeNowPlayingDialog,
  fetchNowPlayingItems,
} from 'src/common/actions/dialog';
import { logMobileWebActivity } from 'src/common/actions/logging';
import cssVariables from 'src/common/styles/variables';
import CommonDialog from '../shared/dialog/CommonDialog';
import ContainerItemsLoader from '../containerItems/ContainerItemsLoader';
import NowPlaying from '../containerItems/childPresentations/NowPlaying';
import ScrollReset from '../shared/ScrollReset';
import NowPlayingContext from '../../contexts/NowPlayingContext';
import SponsoredRollupProvider from '../ads/SponsoredRollupProvider';
import DialogDisplayAd from '../ads/DialogDisplayAd';
import ListLayout from '../containerItems/list/ListLayout';
import { LocationAndLocalizationContext } from '../../providers/LocationAndLocalizationProvider';
import { selectIsDesktopNowPlayingEnabled } from '../../selectors/config';
import { isSmall } from '../../utils/breakpoints';
import { selectIsUserSubscribed } from '../../selectors/me';
import mobileWeb from '../../constants/analytics/categoryActionLabel/mobileWeb';
import { NOW_PLAYING_GUIDE_ID } from '../../constants/dialogs/nowPlaying';
import { NOW_PLAYING_SLOT_NAME_AND_ID } from '../../constants/ads/adSlotNames';
import { RECENTS, NO_RECENTS_TEXT } from '../../constants/localizations/shared';
import ContainerTitle from '../containerItems/shared/ContainerTitle';
import css from './nowPlayingDialog.module.scss';

const idPrefix = 'nowPlayingDialog';
const MAX_RECENTS_COUNT = 5;

// exported for testing
export class NowPlayingDialog extends Component {
  static propTypes = {
    // from parent
    routeProps: PropTypes.object,
    breakpoint: PropTypes.number.isRequired,

    // from mapStateToProps/mapDispatchToProps
    isNowPlayingDialogOpen: PropTypes.bool.isRequired,
    actions: PropTypes.object.isRequired,
    currentlyPlayingGuideId: PropTypes.string.isRequired,
    nowPlayingGuideItem: PropTypes.object.isRequired,
    recentsGuideItem: PropTypes.object.isRequired,
    selectedContentId: PropTypes.string,
    isDesktopNowPlayingEnabled: PropTypes.bool.isRequired,
    isDarkMode: PropTypes.bool,
    isUserSubscribed: PropTypes.bool,
  };

  static contextType = LocationAndLocalizationContext;

  constructor(props) {
    super(props);
    this.handleClose = this.handleClose.bind(this);
    this.scrollToTop = this.scrollToTop.bind(this);
  }

  state = {
    scrollHash: 0,
  };

  componentDidMount() {
    const { isNowPlayingDialogOpen, actions } = this.props;

    // NOTE: Handles directToNowPlaying reporting scenario...
    // where NowPlayingDialog mounts with isNowPlayingDialogOpen set to true via server
    if (isNowPlayingDialogOpen) {
      // eslint-disable-next-line react/no-did-update-set-state
      actions.logMobileWebActivity(
        mobileWeb.actions.show,
        mobileWeb.labels.nowPlayingDialog,
      );
    }
  }

  componentDidUpdate(prevProps) {
    const { isNowPlayingDialogOpen, actions, currentlyPlayingGuideId } =
      this.props;

    if (isNowPlayingDialogOpen && !prevProps.isNowPlayingDialogOpen) {
      // NOTE: Calling fetchNowPlayingItems without currentlyPlayingGuideId will break NowPlaying Containers
      if (currentlyPlayingGuideId) {
        actions.fetchNowPlayingItems(currentlyPlayingGuideId);
      }

      // eslint-disable-next-line react/no-did-update-set-state
      actions.logMobileWebActivity(
        mobileWeb.actions.show,
        mobileWeb.labels.nowPlayingDialog,
      );
    }
  }

  handleClose() {
    this.props.actions.closeNowPlayingDialogAndTrack();
  }

  scrollToTop() {
    this.setState((state) => ({
      scrollHash: state.scrollHash + 1,
    }));
  }

  render() {
    const {
      isNowPlayingDialogOpen,
      breakpoint,
      nowPlayingGuideItem,
      recentsGuideItem,
      currentlyPlayingGuideId,
      routeProps,
      isDesktopNowPlayingEnabled,
      isUserSubscribed,
    } = this.props;
    const { getLocalizedText } = this.context;

    // if this logic is removed we'll need to deregister the ad slot from mint
    if (!isNowPlayingDialogOpen) {
      return null;
    }

    // NOTE: currentlyPlayingGuideId not avail when leveraging SSR for directToNowPlaying UX
    const guideIdToPlay = get(routeProps, 'guideContext.guideId');
    const activeGuideId = currentlyPlayingGuideId || guideIdToPlay;
    const playerHeight = isSmall(breakpoint)
      ? cssVariables['--player-height-small']
      : cssVariables['--player-height'];
    const matchurl = get(routeProps, 'matchUrl', '');

    return (
      <CommonDialog
        modal
        bodyClassName={css.dialog}
        contentClassName={css.dialogContent}
        style={
          isDesktopNowPlayingEnabled
            ? { height: `calc(100vh - ${playerHeight})` }
            : undefined
        }
        overlayClassName={
          isDesktopNowPlayingEnabled ? css.dialogOverlay : undefined
        }
        dialogOpen
        handleDialogClose={this.handleClose}
        data-testid={idPrefix}
        hasDarkTheme
        hideCornerClose
      >
        <div className={css.containerPage}>
          <NowPlayingContext.Provider value={{ scrollToTop: this.scrollToTop }}>
            <ScrollReset
              className={css.scrollReset}
              scrollHash={this.state.scrollHash}
            >
              <div className={css.leftSide}>
                <div className={css.containerItems}>
                  {nowPlayingGuideItem.containerItems ? (
                    <SponsoredRollupProvider guideId={NOW_PLAYING_GUIDE_ID}>
                      <ContainerItemsLoader
                        guideItem={nowPlayingGuideItem}
                        routeProps={routeProps}
                        itemsStyles={get(
                          nowPlayingGuideItem,
                          'metadata.styles',
                          {},
                        )}
                        items={nowPlayingGuideItem.containerItems}
                        isFetching={false}
                        breakpoint={breakpoint}
                        selectedContentId={activeGuideId}
                        isDarkMode
                      />
                    </SponsoredRollupProvider>
                  ) : (
                    <NowPlaying
                      isInNowPlayingDialog
                      hideDisplayAd
                      breakpoint={breakpoint}
                      selectedContentId={activeGuideId}
                      matchUrl={get(routeProps, 'matchUrl', '')}
                    />
                  )}
                </div>
              </div>
            </ScrollReset>
            {isDesktopNowPlayingEnabled && (
              <div className={css.rightSide}>
                {!isUserSubscribed && (
                  <div data-darkmode>
                    <DialogDisplayAd
                      id={NOW_PLAYING_SLOT_NAME_AND_ID}
                      slotName={NOW_PLAYING_SLOT_NAME_AND_ID}
                      isProfile={false}
                      matchurl={matchurl}
                      guideId={activeGuideId}
                      classname={css.ad}
                    />
                  </div>
                )}
                {isUserSubscribed && recentsGuideItem.children && (
                  <div data-darkmode>
                    <ListLayout
                      breakpoint={breakpoint}
                      selectedContentId={activeGuideId}
                      matchUrl={matchurl}
                      id="nowPlayingRecents"
                      containerItem={recentsGuideItem}
                      containerTitle={getLocalizedText(RECENTS)}
                      maxChildren={MAX_RECENTS_COUNT}
                      containerTitleClassName={css.recents}
                    />
                  </div>
                )}
                {isUserSubscribed && !recentsGuideItem.children && (
                  <div data-darkmode>
                    <ContainerTitle
                      containerItem={recentsGuideItem}
                      containerTitle={getLocalizedText(RECENTS)}
                      isNowPlayingDialogOpen
                      actions={{}}
                      className={css.recents}
                    />
                    <div className={css.noRecentsSubtitle}>
                      {getLocalizedText(NO_RECENTS_TEXT)}
                    </div>
                  </div>
                )}
              </div>
            )}
          </NowPlayingContext.Provider>
        </div>
      </CommonDialog>
    );
  }
}

// exported for unit testing
export function mapStateToProps(state) {
  const isNowPlayingDialogOpen = get(
    state,
    'dialog.isNowPlayingDialogOpen',
    false,
  );
  const { player, dialog } = state;
  const currentlyPlayingGuideId = get(player, 'tunedGuideId', '');
  const nowPlayingGuideItem = get(dialog, NOW_PLAYING_GUIDE_ID, {});
  const recentsGuideItem = get(dialog, 'recents', {});

  return {
    isNowPlayingDialogOpen,
    currentlyPlayingGuideId,
    nowPlayingGuideItem,
    recentsGuideItem,
    isDesktopNowPlayingEnabled: selectIsDesktopNowPlayingEnabled(state),
    isUserSubscribed: selectIsUserSubscribed(state),
  };
}

function mapDispatchToProps(dispatch) {
  return {
    actions: bindActionCreators(
      {
        closeNowPlayingDialogAndTrack,
        closeNowPlayingDialog,
        logMobileWebActivity,
        fetchNowPlayingItems,
      },
      dispatch,
    ),
  };
}

export default flow(connect(mapStateToProps, mapDispatchToProps))(
  NowPlayingDialog,
);
